import { useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { CheckCircle, ArrowRight, Warning, FileText, IdentificationCard, Heartbeat, FingerprintSimple, GraduationCap, House } from "@phosphor-icons/react";

const BASE = "https://www.estemsurucukursu.com";

const BELGE_GRUPLARI = [
  {
    icon: IdentificationCard,
    baslik: "Kimlik Belgesi",
    aciklama: "Nüfus cüzdanı (T.C. Kimlik Kartı) veya pasaport. Güncel ve geçerli olmalıdır.",
    zorunlu: true,
    dijital: true,
    detay: "Kimlik kartınızın ön ve arka yüzünün net fotoğrafı veya taraması dijital olarak yüklenebilir.",
  },
  {
    icon: GraduationCap,
    baslik: "Öğrenim Belgesi",
    aciklama: "Lise diploması, mezuniyet belgesi veya öğrenci belgesi. Okur-yazar belgesi de kabul edilir.",
    zorunlu: true,
    dijital: true,
    detay: "e-Devlet üzerinden alınan e-imzalı belge veya okul onaylı belge geçerlidir.",
  },
  {
    icon: Heartbeat,
    baslik: "Sürücü Olur Sağlık Raporu",
    aciklama: "Sürücülüğe engel bir sağlık sorunu olmadığını gösteren resmi rapor.",
    zorunlu: true,
    dijital: false,
    detay: "Herhangi bir devlet hastanesi veya aile sağlığı merkezinden alınabilir. Raporda 'Sürücülüğe engel hali yoktur' ibaresi bulunmalıdır. Muayene ücreti 150–300₺ arasındadır.",
  },
  {
    icon: FingerprintSimple,
    baslik: "Adli Sicil Kaydı",
    aciklama: "Son 3 ay içinde alınmış adli sicil belgesi (sabıka kaydı).",
    zorunlu: true,
    dijital: true,
    detay: "e-Devlet uygulamasından ücretsiz olarak anında alınabilir. Barkodlu/e-imzalı belge geçerlidir.",
  },
  {
    icon: FileText,
    baslik: "Biyometrik Fotoğraf",
    aciklama: "Son 6 ay içinde çekilmiş, biyometrik standartlarda (beyaz arka planlı, yüz açık) 2 adet fotoğraf.",
    zorunlu: true,
    dijital: true,
    detay: "Fotoğraf stüdyosundan veya biyometrik fotoğraf makinelerinden çektirilebilir. Selfie kabul edilmez.",
  },
  {
    icon: House,
    baslik: "İkametgah Belgesi",
    aciklama: "Güncel adres bilginizi gösteren ikametgah belgesi.",
    zorunlu: true,
    dijital: true,
    detay: "e-Devlet üzerinden 'Adres Beyan Sistemi' veya nüfus müdürlüğünden alınabilir. Faturayla ikametgah belgesi olarak başvurmak mümkündür.",
  },
];

export default function KayitEvraklari() {
  useEffect(() => {
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Sürücü kursu kaydı için hangi belgeler gereklidir?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "T.C. kimlik kartı, öğrenim belgesi, sürücü olur sağlık raporu, adli sicil kaydı, 2 adet biyometrik fotoğraf ve ikametgah belgesi gereklidir."
            }
          },
          {
            "@type": "Question",
            "name": "Sağlık raporu nereden alınır?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Herhangi bir devlet hastanesi, özel hastane veya aile sağlığı merkezinden 'sürücü olur raporu' alınabilir."
            }
          },
          {
            "@type": "Question",
            "name": "Adli sicil kaydı nasıl alınır?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "e-Devlet uygulamasından ücretsiz olarak anında alınabilir. Barkodlu e-imzalı belge geçerlidir."
            }
          }
        ]
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Ana Sayfa", "item": BASE },
          { "@type": "ListItem", "position": 2, "name": "Kayıt için Gerekli Evraklar", "item": `${BASE}/kayit-evraklari` }
        ]
      }
    ];
    schemas.forEach((s, i) => {
      const id = `ld-evrak-${i}`;
      let el = document.getElementById(id);
      if (!el) { el = document.createElement("script"); el.id = id; el.type = "application/ld+json"; document.head.appendChild(el); }
      el.textContent = JSON.stringify(s);
    });
    return () => { schemas.forEach((_, i) => { const el = document.getElementById(`ld-evrak-${i}`); if (el) el.remove(); }); };
  }, []);

  return (
    <>
      <SEO
        title="Sürücü Kursu Kaydı İçin Gerekli Evraklar 2026 | Estem Gebze"
        description="Gebze Estem Sürücü Kursu'na kayıt için gerekli belgeler: TC kimlik, öğrenim belgesi, sağlık raporu, adli sicil, fotoğraf, ikametgah. 2026 güncel listesi."
        canonical={`${BASE}/kayit-evraklari`}
        keywords="sürücü kursu evraklar, ehliyet için gerekli belgeler, gebze sürücü kursu belgeler 2026"
      />

      <section className="px-4 md:px-8 py-12 md:py-20 max-w-4xl mx-auto">
        <div className="nb-badge mb-6">Kayıt Evrakları</div>
        <h1 className="font-heading font-bold text-4xl md:text-6xl tracking-tight leading-[1.05]">
          Kaydın İçin<br/>
          <span className="text-brand-primary">Nelere İhtiyacın Var?</span>
        </h1>
        <p className="mt-4 text-lg text-stone-500 max-w-xl">
          Aşağıdaki belgelerle kursumuzda kayıt işleminizi tamamlayabilirsiniz. Dijital yükleme imkanıyla çoğu evrakı online gönderebilirsiniz.
        </p>

        {/* Özet kutu */}
        <div className="mt-8 rounded-2xl bg-brand-primarySubtle border border-orange-200 p-6">
          <div className="font-heading font-bold text-lg mb-3">Kısa Özet — 6 Zorunlu Belge</div>
          <div className="grid md:grid-cols-2 gap-2 text-sm">
            {["T.C. Kimlik Kartı","Öğrenim Belgesi","Sağlık Raporu (sürücü olur)","Adli Sicil Kaydı","2 Biyometrik Fotoğraf","İkametgah Belgesi"].map((b, i) => (
              <div key={i} className="flex items-center gap-2">
                <CheckCircle weight="fill" size={16} className="text-brand-primary shrink-0"/>
                <span>{b}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Belge detayları */}
        <div className="mt-10 space-y-4">
          {BELGE_GRUPLARI.map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={i} className="rounded-2xl border border-stone-200 bg-white p-6 flex gap-5">
                <div className="w-12 h-12 rounded-xl bg-brand-primarySubtle flex items-center justify-center shrink-0">
                  <Icon size={24} weight="duotone" className="text-brand-primary"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="font-heading font-bold text-base">{b.baslik}</span>
                    {b.zorunlu && <span className="text-[10px] font-bold uppercase tracking-wide bg-brand-error/10 text-brand-error px-2 py-0.5 rounded-full">Zorunlu</span>}
                    {b.dijital && <span className="text-[10px] font-bold uppercase tracking-wide bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">Online Yüklenebilir</span>}
                  </div>
                  <p className="text-stone-600 text-sm">{b.aciklama}</p>
                  <p className="text-stone-400 text-xs mt-1.5 leading-relaxed">{b.detay}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Uyarı */}
        <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-5 flex gap-4">
          <Warning size={24} weight="fill" className="text-amber-500 shrink-0 mt-0.5"/>
          <div className="text-sm text-amber-800">
            <span className="font-bold">Önemli not:</span> Sağlık raporunuz fiziksel olarak teslim edilmelidir. Diğer belgeler dijital olarak yüklenebilir. Eksik belgelerle kayıt başlayabilir ancak kursa katılım belge tamamlandıktan sonra gerçekleşir.
          </div>
        </div>

        {/* SSS */}
        <div className="mt-12">
          <h2 className="font-heading font-bold text-2xl mb-5">Sık Sorulan Sorular</h2>
          <div className="space-y-3">
            {[
              { q: "Sağlık raporu ne kadar sürer?", a: "Çoğu sağlık merkezinde aynı gün alınabilir. Göz ve nöroloji muayenesi içerir, toplam süre 30–60 dakikadır." },
              { q: "Öğrenim belgesini nereden alırım?", a: "e-Devlet'ten (turkiye.gov.tr) 'Diploma Sorgulama' bölümünden veya mezun olduğunuz okuldan alabilirsiniz." },
              { q: "Yabancı uyruklu adaylar için belgeler farklı mı?", a: "Evet. TC kimlik yerine ikamet izni ve pasaport gerekir. Detay için ofisimizi arayınız." },
              { q: "Belgelerim eksikse kaydımı başlatabilir miyim?", a: "Evet. Online kayıt başlayabilir ancak eksik belgeleri kurs başlamadan önce tamamlamanız gerekir." },
            ].map((f, i) => (
              <details key={i} className="rounded-xl border border-stone-200 bg-white group">
                <summary className="px-5 py-4 cursor-pointer font-semibold text-sm flex items-center justify-between gap-4 list-none">
                  {f.q}
                  <span className="shrink-0 text-brand-primary group-open:rotate-180 transition-transform inline-block">▾</span>
                </summary>
                <div className="px-5 pb-4 text-stone-500 text-sm border-t border-stone-100 pt-3">{f.a}</div>
              </details>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 rounded-2xl bg-brand-ink text-white p-8 flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <div className="font-heading font-bold text-2xl">Belgelerini Hazırladın mı?</div>
            <p className="text-white/70 text-sm mt-1">Online kayıt 5 dakika sürer. Dijital belgelerini hemen yükle.</p>
          </div>
          <Link to="/basla" className="shrink-0 nb-btn !px-8 !py-4 !text-base">
            Kayıt Ol <ArrowRight weight="bold"/>
          </Link>
        </div>
      </section>
    </>
  );
}
